<template>
  <transition name="anchor-nav-fade">
    <div
      v-if="visible && cities.length > 1"
      v-city-tab-view="analyticsBase"
      class="anchor-nav"
    >
      <PageWrap>
        <nav
          ref="listRef"
          class="anchor-nav__list"
        >
          <div
            v-for="(city, idx) in cities"
            :key="cityKey(city) || idx"
            v-city-tab-click="clickParams(city)"
            class="anchor-nav__item"
            :class="{ 'anchor-nav__item--active': cityKey(city) === activeKey }"
            @click="emit('select', city)"
          >
            {{ city.name }}
          </div>
        </nav>
      </PageWrap>
    </div>
  </transition>
</template>

<script setup lang="ts">
  import { nextTick, ref, watch } from 'vue';

  import type { NearbyCity } from '@/shared/api/generated/api';
  import { useClickTracker, useViewTracker } from '@/shared/lib/analytics/compat';
  import PageWrap from '@/shared/ui/PageWrap.vue';

  const props = withDefaults(
    defineProps<{
      cities?: NearbyCity[];
      activeKey?: string;
      visible?: boolean;
      analyticsBase?: Record<string, unknown>;
    }>(),
    {
      cities: () => [],
      activeKey: '',
      visible: false,
      analyticsBase: () => ({}),
    },
  );

  const emit = defineEmits<{
    (e: 'select', city: NearbyCity): void;
  }>();

  const vCityTabView = useViewTracker('around:city-tab.view');
  const vCityTabClick = useClickTracker('around:city-tab.click');

  const listRef = ref<HTMLElement | null>(null);

  const cityKey = (city: NearbyCity): string => city.url || city.name || '';

  // Подкручиваем горизонтальное меню так, чтобы активный пункт был в зоне видимости
  // (например, при вертикальном скролле страницы, когда пунктов много).
  const scrollActiveIntoView = () => {
    const list = listRef.value;

    if (!list) return;

    const activeEl = list.querySelector<HTMLElement>('.anchor-nav__item--active');

    if (!activeEl) return;

    const listRect = list.getBoundingClientRect();
    const itemRect = activeEl.getBoundingClientRect();

    // Позиция пункта относительно контента скролл-контейнера
    const itemLeftInList = itemRect.left - listRect.left + list.scrollLeft;
    const targetLeft = itemLeftInList - (list.clientWidth - activeEl.clientWidth) / 2;
    const maxLeft = list.scrollWidth - list.clientWidth;
    const left = Math.max(0, Math.min(targetLeft, maxLeft));

    list.scrollTo({ left, behavior: 'smooth' });
  };

  watch(
    () => [props.activeKey, props.visible] as const,
    async () => {
      if (!props.visible) return;

      await nextTick();
      scrollActiveIntoView();
    },
  );

  const clickParams = (city: NearbyCity): Record<string, unknown> => ({
    ...props.analyticsBase,
    subject_geo_name: city.name,
    subject_geo_type: 'city',
  });
</script>

<style scoped lang="scss">
  .anchor-nav {
    position: fixed;
    top: var(--around-anchor-nav-top, 0);
    right: 0;
    left: 0;
    z-index: 40;
    gap: var(--alias-space-16, 16px);
    background: var(--alias-color-background-default);

    &__list {
      display: flex;
      gap: var(--alias-space-16, 16px);
      align-items: flex-start;
      align-self: stretch;
      padding: var(--alias-space-0, 0) var(--wrap-offset, 20px);

      // Растягиваем область скролла до краёв экрана (компенсируем паддинг Wrap),
      // а отступ 20px переносим в padding самого скролл-контейнера. Тогда крайние города
      // стоят с отступом 20px, но при скролле текст уезжает за край экрана, а не обрезается.
      margin: 0 calc(-1 * var(--wrap-offset, 20px));
      overflow-x: auto;
      scrollbar-width: none;
      border-bottom: 1px solid var(--alias-color-border-default);

      &::-webkit-scrollbar {
        display: none;
      }
    }

    &__item {
      padding: 14px 0;
      color: var(--comp-tabs-color-label);
      white-space: nowrap;
      cursor: pointer;
      background: none;
      border: none;

      &--active {
        color: var(--comp-tabs-color-label-active);
        border-bottom: 2px solid var(--comp-tabs-color-indicator);
      }

      @include base-400-300-medium;
    }
  }

  .anchor-nav-fade-enter-active,
  .anchor-nav-fade-leave-active {
    transition: opacity 0.2s ease;
  }

  .anchor-nav-fade-enter-from,
  .anchor-nav-fade-leave-to {
    opacity: 0;
  }
</style>
